import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Linkedin } from 'lucide-react';
import { clinicInfo } from '../data/mock';

export const Footer = () => {
  const quickLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/doctors', label: 'Our Doctors' },
    { path: '/symptoms', label: 'Symptom Guides' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <footer className="bg-[#1C5C62] text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_care-staging/artifacts/bh07xo4f_hw_logo_white.png" 
              alt="Healthwrx" 
              className="h-10 w-auto mb-4"
            />
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              {clinicInfo.tagline}
            </p>
            <p className="text-gray-300 text-sm leading-relaxed">
              Quality healthcare services with a patient-first approach.
            </p>

            {/* Social profiles */}
            <div className="flex items-center gap-3 mt-5" data-testid="footer-social-links">
              <a
                href="https://www.instagram.com/healthwrx"
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Healthwrx on Instagram"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[#14D1A2] hover:text-white text-gray-200 transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/share/1RPWSbDz5r/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Healthwrx on Facebook"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[#14D1A2] hover:text-white text-gray-200 transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/healthwrx"
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Healthwrx on LinkedIn"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[#14D1A2] hover:text-white text-gray-200 transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-300 text-sm hover:text-[#14D1A2] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://www.averrun.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 text-sm hover:text-[#14D1A2] transition-colors"
                >
                  Preventive & Long-Term Care
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#14D1A2] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">
                  {clinicInfo.address.line1}<br />
                  {clinicInfo.address.line2}<br />
                  {clinicInfo.address.city} {clinicInfo.address.pincode}
                  <br />
                  <a
                    href="https://share.google/qNd3DAEdwcdrcygx4"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-1 text-[#14D1A2] hover:underline text-xs font-medium"
                    data-testid="footer-gbp-link"
                  >
                    Get directions on Google Maps →
                  </a>
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[#14D1A2] flex-shrink-0" />
                <a href={`tel:${clinicInfo.phone}`} className="text-gray-300 text-sm hover:text-[#14D1A2] transition-colors">
                  {clinicInfo.phone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-[#14D1A2] flex-shrink-0" />
                <a href={`mailto:${clinicInfo.email}`} className="text-gray-300 text-sm hover:text-[#14D1A2] transition-colors">
                  {clinicInfo.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Opening Hours</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-[#14D1A2] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-white font-medium">Monday - Saturday</p>
                  <p className="text-gray-300">{clinicInfo.hours.weekdays}</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-[#14D1A2] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-white font-medium">Sunday</p>
                  <p className="text-gray-300">{clinicInfo.hours.sunday}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-600 mt-12 pt-8 text-center space-y-3">
          <p className="text-gray-400 text-xs max-w-3xl mx-auto leading-relaxed" data-testid="footer-disclaimer">
            <strong>Medical disclaimer:</strong> The content on this website is for general informational purposes only and is not a substitute for professional medical advice, diagnosis or treatment. Always consult a qualified healthcare provider for any questions about a medical condition.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-300" data-testid="footer-legal-links">
            <Link to="/privacy-policy" className="hover:text-[#14D1A2] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <span aria-hidden="true" className="text-gray-500">·</span>
            <Link to="/terms-of-service" className="hover:text-[#14D1A2] transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
            <span aria-hidden="true" className="text-gray-500">·</span>
            <Link to="/editorial-policy" className="hover:text-[#14D1A2] transition-colors" data-testid="footer-editorial-link">Editorial Policy</Link>
          </div>
          <p className="text-gray-300 text-sm">
            © {new Date().getFullYear()} Healthwrx. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
